import {
  Colors,
  Divider,
  Flex,
  IconDownload,
  IconInfo,
  Spinner,
  Tag,
  Typography,
  VerticalSpacing,
} from '@banhala/ui-kit';
import { bridge, isNil, localizeNumber } from 'shared/src/utils';
import { BrandIconFreeDelivery } from '../BrandIconFreeDelivery';
import React, { useCallback, useEffect, useMemo } from 'react';
import type { GoodsOption } from 'shared/src/api';
import { useGoodsOptionsPricing } from '../../queries/useGoodsOptionsPricing';
import { useMyInfo } from 'shared/src/queries';
import { usePostBatchRedeemCoupon } from '../../queries/usePostBatchRedeemCoupon';
import { useIsBottomSheetGiftMode } from '../../hooks/useIsBottomSheetGiftMode';

const CouponTagView: React.FC<{ selected: GoodsOption[] }> = props => {
  const { data: myInfo } = useMyInfo();

  const { data: pricing, isLoading } = useGoodsOptionsPricing({
    options: props.selected.map(option => ({
      sno: option.sno,
      count: option.purchase_count ?? 1,
    })),
  });
  const {
    mutateAsync: postBatchRedeemCoupon,
    isPending: isLoadingPostBatchRedeemCoupon,
  } = usePostBatchRedeemCoupon(props.selected);

  const isAllIssued = useMemo(() => {
    if (!pricing) return false;
    return (
      pricing.coupon_list.filter(coupon => coupon.item.is_issued).length ===
      pricing.coupon_list.length
    );
  }, [pricing]);

  const onPressRedeemCoupon = useCallback(async () => {
    if (myInfo.is_anonymous) {
      bridge.assertLogin({
        message: '로그인을 해야 이용 할 수 있습니다.',
      });
      return;
    }

    await postBatchRedeemCoupon();
  }, [myInfo.is_anonymous, postBatchRedeemCoupon]);

  useEffect(() => {
    if (isLoading || isNil(pricing)) return;
    if (pricing.coupon_list.length === 0) return;
    if (isAllIssued) return;

    //DISPLAY_COUPON 찍기
    bridge.logAnalyticsEvent({
      name: 'DISPLAY_COUPON',
      analytics: { ...pricing.coupon?.logging.analytics },
    });
  }, [isAllIssued, isLoading, pricing]);

  if (myInfo.is_anonymous) {
    return (
      <Flex onPress={onPressRedeemCoupon}>
        <Tag color="blue" size="small" type="secondary" icon={IconDownload}>
          최대 할인 쿠폰 받기
        </Tag>
      </Flex>
    );
  }

  if (isLoading || isNil(pricing) || isLoadingPostBatchRedeemCoupon) {
    return (
      <Flex.Center>
        <Spinner size={14} />
      </Flex.Center>
    );
  }
  if (pricing.coupon_list.length === 0) {
    //쿠폰이 없으면
    return (
      <Typography color={'content_tertiary'} type={'body2'}>
        사용 가능한 쿠폰 없음
      </Typography>
    );
  }

  if (isAllIssued) {
    return (
      <Flex.Row alignItems={'center'} gap={8}>
        {pricing.coupon_list.length > 1 ? (
          <Flex.Row alignItems={'center'} gap={8}>
            <Tag color="slate" size="small" type="secondary">
              중복할인
            </Tag>
            <Tag color="pink" size="small" type="secondary">
              최대할인
            </Tag>
          </Flex.Row>
        ) : (
          <Tag color="pink" size="small" type="secondary">
            최대 할인 적용
          </Tag>
        )}
        <Typography type={'body2'}>
          -{localizeNumber(pricing.discount_amount)}원
        </Typography>
      </Flex.Row>
    );
  }

  // return <Tag>최대 할인 쿠폰 받기</Tag>;

  return (
    <Flex onPress={onPressRedeemCoupon}>
      <Tag color="blue" size="small" type="secondary" icon={IconDownload}>
        최대 할인 쿠폰 받기
      </Tag>
    </Flex>
  );
};

export const PricingInfo: React.FC<{ selected: GoodsOption[] }> = props => {
  const { data: pricing } = useGoodsOptionsPricing({
    options: props.selected.map(option => ({
      sno: option.sno,
      count: option.purchase_count ?? 1,
    })),
  });
  const goodsPrice = useMemo(() => {
    return props.selected.reduce(
      (acc, cur) => acc + cur.price * (cur.purchase_count ?? 1),
      0,
    );
  }, [props.selected]);
  const discountPrice = useMemo(() => {
    return pricing?.discount_amount ?? 0;
  }, [pricing?.discount_amount]);

  const totalPrice = useMemo(() => {
    return goodsPrice - discountPrice;
  }, [discountPrice, goodsPrice]);

  const isGiftMode = useIsBottomSheetGiftMode();

  if (isGiftMode) {
    return (
      <Flex padding={{ horizontal: 20, vertical: 16 }} gap={12}>
        <Flex.Row alignItems={'center'} gap={4}>
          <IconInfo size={16} fill={Colors.content_secondary} />
          <Typography type={'body2-relaxed'} color={'content_secondary'}>
            모든 선물 상품은 일반 배송으로 부내드려요
          </Typography>
        </Flex.Row>
        <Divider size={'small'} />
        <Flex gap={8}>
          <Flex.Row justifyContent={'space-between'}>
            <Typography type={'subtitle1'}>예상 결제금액</Typography>
            <Typography type={'h4'}>{localizeNumber(totalPrice)}원</Typography>
          </Flex.Row>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex padding={{ horizontal: 20, vertical: 16 }}>
      <Flex.Row
        justifyContent={'space-between'}
        height={30}
        alignItems={'center'}
      >
        <Typography type={'body2'} color={'content_tertiary'}>
          총 {props.selected.length}개 상품
        </Typography>
        <Typography type={'body2'}>{localizeNumber(goodsPrice)}원</Typography>
      </Flex.Row>
      <Flex.Row
        justifyContent={'space-between'}
        height={30}
        alignItems={'center'}
      >
        <Typography type={'body2'} color={'content_tertiary'}>
          쿠폰 할인
        </Typography>
        <CouponTagView selected={props.selected} />
      </Flex.Row>
      <VerticalSpacing size={12} />
      <Divider size={'small'} />
      <VerticalSpacing size={12} />
      <Flex gap={8}>
        <Flex.Row justifyContent={'space-between'}>
          <Typography type={'subtitle1'}>예상 결제금액</Typography>
          <Typography type={'h4'}>{localizeNumber(totalPrice)}원</Typography>
        </Flex.Row>
        <Flex.Row justifyContent={'flex-end'}>
          <BrandIconFreeDelivery />
        </Flex.Row>
      </Flex>
    </Flex>
  );
};
